import { Icon, Icons } from "@ir/ui";
import "./Navigation.scss";
import type { NavigationItemProps } from "./types";
import { useCallback, type FC } from "react";
import { useAppNavigation } from "./hooks";
import NavigationItem from "./NavigationItem";
import { Permissions } from "@src/domain/permissions/Permissions";
import { useUserPermissions } from "@src/presentation/hooks/permissions/useUserPermissions";

const NAVIGATION_ITEMS: NavigationItemProps[] = [
  {
    key: "organizations",
    label: "Organizations & Brands",
    icon: <Icon name={Icons.Organization} />,
    link: `/organizations`,
    requiredPermissions: [Permissions.ORGANIZATIONS_VIEW],
  },
  {
    key: "users-access",
    label: "Users & Access",
    icon: <Icon name={Icons.UserManagement} />,
    link: `/users-access`,
    requiredPermissions: [Permissions.USER_MANAGEMENT_VIEW],
  },
  {
    key: "role-permissions",
    label: "Role Permissions",
    icon: <Icon name={Icons.UserPermission} />,
    link: `/role-permissions`,
    requiredPermissions: [Permissions.USER_MANAGEMENT_VIEW],
  },
  {
    key: "purchasers",
    label: "Purchasers",
    icon: <Icon name={Icons.Purchaser} />,
    link: `/purchasers`,
    requiredPermissions: [Permissions.PURCHASERS_VIEW],
  },
  {
    key: "master-data",
    label: "Master Data",
    icon: <Icon name={Icons.Rows01} />,
    link: `/master-data`,
    requiredPermissions: [Permissions.MASTER_DATA_VIEW],
  },
  {
    key: "configuration",
    label: "Configuration",
    icon: <Icon name={Icons.Settings} />,
    link: `/configuration`,
    requiredPermissions: [Permissions.CONFIG_VIEW],
  },
  {
    key: "dashboard",
    label: "Dashboard",
    icon: <Icon name={Icons.BarChart5} />,
    link: `/dashboard`,
    requiredPermissions: [Permissions.DASHBOARD_VIEW],
  },
];

const SlideNav: FC = () => {
  const { hasAnyPermission } = useUserPermissions();

  const filterMenu = useCallback(() => {
    const shouldIncludeItem = (item: NavigationItemProps): boolean => {
      if (!item.requiredPermissions) {
        return false;
      }
      return hasAnyPermission(item.requiredPermissions);
    };

    const checkPermissionsOfMenuItems = (
      sideNavItems: NavigationItemProps[],
    ): NavigationItemProps[] => {
      const navItems: NavigationItemProps[] = [];
      for (const item of sideNavItems) {
        if (shouldIncludeItem(item)) {
          navItems.push(item);
        }
        if (item.children) {
          item.children = checkPermissionsOfMenuItems(item.children);
        }
      }
      return navItems;
    };

    return checkPermissionsOfMenuItems(NAVIGATION_ITEMS);
  }, [hasAnyPermission]);

  const {
    isItemActive,
    handleItemClick,
    isCurrentLinkActive,
    toggleItem,
    isItemOpen,
    openItems,
  } = useAppNavigation();

  return (
    <nav className="slide-navigation">
      <ul className="m-0 p-0">
        {filterMenu().map((item) => (
          <NavigationItem
            key={item.key}
            item={item}
            isItemActive={isItemActive}
            isCurrentLinkActive={isCurrentLinkActive}
            toggleItem={toggleItem}
            isItemOpen={isItemOpen}
            handleItemClick={handleItemClick}
            openItems={openItems}
          />
        ))}
      </ul>
    </nav>
  );
};

export default SlideNav;
